<template>
    <div>
        <div v-if="!loading" class="flex justify-content-start flex-column sm:flex-row">
            <Button label="Send Notification" class="p-button-primary mr-2" @click="toggleNotificationDialog" />
            <Button :label="`Copy ${label('Task')}`" class="p-button-warning mr-2" @click="copyTask" />
        </div>
        <DataTable
            v-model:selection="selectedTasks"

            :multiSortMeta="preSort"
            v-model:first="offset"
            :rows="numberOfRows"

            selection="any"
            selectionMode="multiple"
            :value="allTasks"
            :lazy="true"
            :paginator="true"

            :rowClass="rowClass"
            ref="dt"
            dataKey="id"
            :totalRecords="totalRecords"
            :loading="loading"
            @page="onPage($event)"
            @sort="onSort($event)"
            removableSort
            sortMode="multiple"
            responsiveLayout="scroll"
            :rowsPerPageOptions="[5, 10, 20, 50, 100]"
            paginatorTemplate="CurrentPageReport JumpToPageInput FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries :: Page No:"
        >
            <template #empty> No {{ label('Tasks') }} available. </template>
            <template #loading> Loading {{ label('Tasks') }}. Please wait. </template>
            <Column selectionMode="multiple" headerStyle="width: 2%"></Column>
            <Column field="id" header="ID" sortable style="width: 4%"></Column>
            <Column field="title" :header="`${label('Task').toUpperCase()} TITLE`" sortable style="width: 8%" />
            <Column field="description" header="DESCRIPTION" style="width: 18%">
                <template #body="slotProps">
                    <div v-if="slotProps.data.description" class="task-description-container overflow-x-scroll">
                        <!-- v-html="slotProps.data.description" /> -->
                        <Accordion>
                            <AccordionTab :header="getTaskHeader(slotProps.data.description)">
                                <div v-html="slotProps.data.description" />
                            </AccordionTab>
                        </Accordion>
                    </div>
                    <div v-else>No {{ label('Task') }} description...</div>
                </template>
            </Column>

            <Column field="assigned_to" header="ASSIGNED TO" sortable style="width: 12%">
                <template #body="slotProps">{{ slotProps.data.assigned_user }} </template>
            </Column>

            <!-- <Column field="assigned_by" header="ASSIGNED BY" style="max-width: 6rem" /> -->
            <Column field="task_progress" header="STATUS" style="width: 12%">
                <template #body="slotProps">
                    <span class="status-tooltip-wrap" tabindex="0" :aria-label="getLastActivitiesAriaLabel(slotProps.data)" @mouseenter="loadLastActivity(slotProps.data)" @focus="loadLastActivity(slotProps.data)">
                        <span v-if="!slotProps.data.archived_at" :class="'status-badge status-' + slotProps.data.task_progress?.status">{{ slotProps.data.task_progress?.name }}</span>
                        <span v-else :class="'status-badge status-inprogress'">Archived</span>
                        <span class="status-tooltip" role="tooltip">
                            <span class="status-tooltip__title">Last Activities</span>
                            <span v-for="activity in getLastActivities(slotProps.data)" :key="activity.label" class="status-tooltip__row">
                                <span class="status-tooltip__label">
                                    <i :class="activity.icon"></i>
                                    <span>{{ activity.label }}</span>
                                </span>
                                <span class="status-tooltip__date">{{ activity.date ? formatDateTime(activity.date) : '—' }}</span>

                            </span>
                            <span class="status-tooltip__arrow" aria-hidden="true"></span>
                        </span>
                    </span>
                </template>
            </Column>
            <Column v-if="isFeatureEnabled" field="assurance_level" header="PROGRESS TO COMPLETION" sortable style="width: 8%">
                <template #body="slotProps">
                    <span
                        v-if="assuranceLabels[slotProps.data.assurance_level] || getAssuranceLabel(slotProps.data.assurance_level)"
                        :class="'assurance-badge badge-' + slotProps.data.assurance_level" :style="assuranceColors[slotProps.data.assurance_level] ? { backgroundColor: assuranceColors[slotProps.data.assurance_level] } : {}"
                    >
                        {{ assuranceLabels[slotProps.data.assurance_level] || getAssuranceLabel(slotProps.data.assurance_level) }}
                    </span>
                </template>
            </Column>
            <Column field="priority" header="PRIORITY" sortable style="width: 8%">
                <template #body="slotProps">
                    <span :class="'priority-badge status-' + slotProps.data.task_priority?.toLowerCase()">{{ slotProps.data.task_priority }}</span>
                </template>
            </Column>
            <!-- <Column field="created_at" header="CREATED AT" sortable style="width: 7rem" /> -->
            <Column field="due_date" header="DUE DATE" sortable style="width: 10%">
                <template #body="slotProps">{{ slotProps.data.due_date_display }} </template>
            </Column>
            <Column field="theme_name" header="THEME" style="width: 10%" />
            <Column field="actions" header="ACTIONS" style="width: 18%">
                <template #body="slotProps">
                    <span class="p-column-title">ACTIONS</span>
                    <div class="flex flex-nowrap align-items-center">
                        <Button v-show="authStore.hasPermission(UserPermission.READ_TASK)" icon="pi pi-eye" class="p-button-rounded p-button-info mr-1" @click="viewDetails(slotProps.data.id)"  v-tooltip="`View ${label('Task')}`" />
                        <Button v-show="slotProps.data.editable && authStore.hasPermission(UserPermission.WRITE_TASK)" icon="pi pi-pencil" class="p-button-rounded p-button-success mr-1" @click="editTask(slotProps.data.id)"  v-tooltip="`Edit ${label('Task')}`" />
                        <Button v-show="slotProps.data.progress == 3 && (authStore.hasPermission(UserPermission.ALLOW_ARCHIVE_TASK) || taskStore.isTaskOwnerOrSelf(slotProps.data.author_id))"
                            :icon="slotProps.data.archived_at ? 'pi pi-folder-open' : 'pi pi-folder-plus'"
                            class="p-button-rounded p-button-warning mr-1"
                            @click="handleArchiveAction(slotProps.data.id, slotProps.data.archived_at)"
                            v-tooltip="slotProps.data.archived_at ? 'Unarchive' : 'Archive'"
                        />
                        <Button v-show="authStore.hasPermission(UserPermission.DELETE_TASK)" icon="pi pi-trash" class="p-button-rounded p-button-danger" @click="confirmRequest({ id: slotProps.data.id }, taskStore.deleteTask)" v-tooltip="`Delete ${label('Task')}`" />
                    </div>
                </template>
            </Column>
        </DataTable>
        <task-notification :task-ids="selectedTasks" v-if="showNotificationDialog" @hide-dialog="showNotificationDialog = false" />
        <task-archive-reason v-if="showArchiveDialog"  :action-type="archiveActionType" :taskId="selectedTaskId" @comment-added="archiveReasonAdded" @cancelled="showArchiveDialog = false" @hide-dialog="showArchiveDialog = false" />
    </div>
</template>
<script setup>
import TaskNotification from './TaskNotification.vue';

import { computed, onMounted, onUnmounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';

import { authStore, commentStore, companyStore, taskStore } from '../../../../../store/global-stores';
import { showToast } from '../../../../../utilities/toast-handler';
import { TaskStatus } from '../../debrief/enum';
import { removeHtmlTagsFromParagraph, truncateText } from '../../../../../utilities';
import { UserPermission } from '../../../../../modules/permission/enum';
import { useConfirmRemove } from '../../../../../composables/ConfirmComposable';
import {
    buildPagination,
    getOrder,
    getPage,
    getPerPage,
    setOrder,
    setPage,
    setPerPage,
} from '@/composables/PaginationComposable';
import { getAssuranceLabel } from '../../../constants';
import { formatDateTime } from '@/utilities/date-formatter';
import { authApi } from '@/utilities/axios';
import { useAssuranceColors } from '@/composables/useAssuranceColors';
import TaskArchiveReason from '@/product/ams/modules/task/components/TaskArchiveReason.vue';
import { useLabels } from '@/composables/LabelComposable';

const { label } = useLabels();
const router = useRouter();
const { allTasks, totalRecords, params, loading } = storeToRefs(taskStore);
const { confirmRequest } = useConfirmRemove();
const { assuranceColors, assuranceLabels } = useAssuranceColors();

const selectedTasks = ref([]);
const showNotificationDialog = ref(false);

const showArchiveDialog = ref(false);
const selectedTaskId = ref(null);
const archiveActionType = ref('archive'); // or 'unarchive'

const offset = computed(() => {
    return getPage('taskList') * getPerPage('taskList')
})

const preSort = computed(() => {
    return getOrder('taskList')
})

const numberOfRows = computed(() => {
    return getPerPage('taskList')
})

const isFeatureEnabled = companyStore.useFeatureAccess('assurance-level');
const { lastAddedComment } = storeToRefs(commentStore);

const lastActivityByTask = ref({});
const lastActivityLoading = ref({});

const buildTaskUpdateActivity = (task) => {
    const taskTimestamp = Date.parse(task?.updated_at);

    return {
        label: 'Task Update',
        date: task?.updated_at,
        icon: 'pi pi-pencil',
        time: Number.isFinite(taskTimestamp) ? taskTimestamp : 0,
    };
};

const getLastActivities = (task) => {
    return lastActivityByTask.value[task?.id] ?? [buildTaskUpdateActivity(task)];
};

const getLastActivitiesAriaLabel = (task) => {
    const activities = getLastActivities(task)
        .map((activity) => `${activity.label} ${activity.date ? formatDateTime(activity.date) : 'no activity'}`)
        .join(', ');

    return `Last Activities: ${activities}`;
};

const loadLastActivity = async (task) => {
    const taskId = task?.id;

    if (!taskId || lastActivityByTask.value[taskId] || lastActivityLoading.value[taskId]) {
        return;
    }

    lastActivityLoading.value[taskId] = true;

    try {
        const response = await authApi.get(`${process.env.VUE_APP_API_VERSION}/tasks/${taskId}/comments`, {
            params: {
                companyId: authStore.getLoggedUserCompanyId,
            },
        });

        const comments = response?.data ?? [];
        const activities = [buildTaskUpdateActivity(task)];

        let latestComment = null;
        let latestAttachment = null;
        let latestArchive = null;

        comments.forEach((comment) => {
            const rawDate = comment?.updated_at || comment?.created_at;
            const timestamp = Date.parse(rawDate);

            if (!Number.isFinite(timestamp)) {
                return;
            }

            if (comment?.type === 'archive' || comment?.type === 'unarchive') {
                if (!latestArchive || timestamp > latestArchive.time) {
                    latestArchive = {
                        label: comment.type === 'archive' ? 'Archived' : 'Unarchived',
                        date: rawDate,
                        icon: comment.type === 'archive' ? 'pi pi-folder' : 'pi pi-folder-open',
                        time: timestamp,
                    };
                }
            } else if (!latestComment || timestamp > latestComment.time) {
                latestComment = {
                    label: 'Comment',
                    date: rawDate,
                    icon: 'pi pi-comment',
                    time: timestamp,
                };
            }

            if (comment?.attachment?.length && (!latestAttachment || timestamp > latestAttachment.time)) {
                latestAttachment = {
                    label: 'Attachment',
                    date: rawDate,
                    icon: 'pi pi-paperclip',
                    time: timestamp,
                };
            }
        });

        if (latestComment) {
            activities.push(latestComment);
        }

        if (latestAttachment) {
            activities.push(latestAttachment);
        }

        if (latestArchive) {
            activities.push(latestArchive);
        }

        lastActivityByTask.value[taskId] = activities.sort((a, b) => b.time - a.time);
    } catch (error) {
        lastActivityByTask.value[taskId] = [buildTaskUpdateActivity(task)];
    } finally {
        lastActivityLoading.value[taskId] = false;
    }
};

onMounted(async () => {
    buildPagination("taskList", 0, 10, [{field: "id", order: -1}])

    taskStore.initparams();
    await companyStore.fetchCompanyPreferences();
    loadTasks();
});

onUnmounted(() => {
    taskStore.initparams();
});

const getTaskHeader = (dirtyDescription) => {
    const sanitizeParagraph = removeHtmlTagsFromParagraph(dirtyDescription);
    return truncateText(sanitizeParagraph, 30);
};

const loadTasks = async () => {
    await taskStore.getExistingTasks('taskList');
};

const onPage = (event) => {
    params.value = event;
    //onSort(event);
    setPage('taskList', event.page)
    setPerPage('taskList', event.rows)
    loadTasks();
};

const onSort = (event) => {
    event.multiSortMeta.forEach((sort) => {
        console.log('sort', sort.field, sort.order);
    })

    setOrder('taskList', event.multiSortMeta)
    loadTasks();
};

const rowClass = (data) => {
    const dueDate = new Date(data.due_date);
    const today = new Date();
    const nearDueDate = new Date(today);
    nearDueDate.setDate(today.getDate() + 14);
    // check if tasks which are not completed are overdue already
    if (today > dueDate && parseInt(data.progress) !== 3) {
        return [{ 'overdue-tasks': true }];
    }
    // check if task's due date is near due date i.e 14 days
    else if (dueDate.toISOString() <= nearDueDate.toISOString() && parseInt(data.progress) !== 3) {
        return [{ 'near-overdue-tasks': true }];
    }
};

const editTask = (id) => {
    router.push({ name: 'TaskForm', query: { taskId: id } });
};

const viewDetails = (id) => {
    router.push({ name: 'TaskDetails', params: { id } });
};

const toggleNotificationDialog = () => {
    if (selectedTasks.value.length <= 0) {
        showToast({ severity: 'info', summary: `${label('Tasks')} not selected` }, `Select at least one ${label('Task').toLowerCase()}`);
    } else {
        showNotificationDialog.value = true;
    }
};

// migrate this function later into CloneTask component
const copyTask = async () => {
    // 1) Check if any task has been selected or not first
    if (selectedTasks.value.length <= 0) {
        showToast({ severity: 'info', summary: `Issue with ${label('Task')} selection` }, `Select at least one ${label('Task').toLowerCase()}`);
    }
    // 2) Check if more than one task have been selected
    else if (selectedTasks.value.length > 1) {
        showToast({ severity: 'info', summary: `Issue with ${label('Task')} selection` }, `Select only one ${label('Task').toLowerCase()} at a time`);
    }
    // 3) Start copying the task information and send it as a params or setup payload
    else {
        const currTaskId = selectedTasks.value[0]?.id;
        const response = await taskStore.fetchTaskDetails(currTaskId);
        if (response) {
            // debugger;
            const taskBody = { ...response, title: 'Copy ' + response.title, progress: TaskStatus.NOPROGRESS, to_department_id: null, users_assigned_to: [], users_notified_to: [] }; //cloned tasks progress begins with status No progress
            taskStore.loadTaskBody(taskBody);
            router.push({ name: 'TaskForm', query: { copyMode: true } });
        }
    }
};

const handleArchiveAction = (taskId, isArchived) => {
    selectedTaskId.value = taskId;
    archiveActionType.value = isArchived ? 'unarchive' : 'archive';
    showArchiveDialog.value = true;
};

const archiveReasonAdded = async () => {
    let lastAddedCommentId = lastAddedComment.value.id;
    taskStore.onArchiveTask(lastAddedCommentId, archiveActionType, selectedTaskId.value);
    showArchiveDialog.value = false;
    loadTasks();
};


</script>
<style lang="scss">
.task-description-container {
    max-height: 20rem !important;
}
// .p-datatable .p-datatable-tbody > tr takes precedence over the following class so its set to important here
.overdue-tasks {
    background-color: #f2c5c9 !important;
    font-weight: bold;
}
.near-overdue-tasks {
    background-color: #f5ccaea8 !important;
}
.status-badge {
    border-radius: var(--border-radius);
    padding: 0.25em 0.5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.3px;

    &.status-noprogress {
        background: #ffcdd2;
        color: #c63737;
    }
    &.status-inprogress {
        background: #feedaf;
        color: #8a5340;
    }
    &.status-completed {
        background: #c8e6c9;
        color: #256029;
    }
}

.priority-badge {
    border-radius: var(--border-radius);
    padding: 0.25em 0.5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.3px;

    &.status-low {
        background: #b3e5fc;
        color: #23547b;
    }

    &.status-medium {
        background: #feedaf;
        color: #8a5340;
    }
    &.status-high {
        background: #ffcdd2;
        color: #c63737;
    }
}

.assurance-badge {
    display: inline-block;
    padding: 0.3rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    border-radius: 999px; /* pill-shaped */
    text-align: center;
    text-transform: capitalize;
    white-space: nowrap;
    color: white;
}

/* Level-specific colors */
.badge-inadequate {
    background-color: #d32f2f; /* Red */
}

.badge-requires_improvement {
    background-color: #c62828; /* Dark Red */
}

.badge-adequate {
    background-color: #f57c00; /* Orange */
}

.badge-good {
    background-color: #43a047; /* Light Green */
}

.badge-outstanding {
    background-color: #2e7d32; /* Dark Green */
}

</style>
